import { useEffect, useState } from 'react';
import {
  postRequestWithRetry,
  requestWithRetry,
} from 'src/commons/utils/asyncRequest';
import { logger } from 'src/commons/utils/logger';
import { usePageTitleIconContext } from 'src/features/header/components/PageHeader/PageTitleIcon/PageTitleIconContext';
import { emitPageTitleIconToggle } from 'src/features/header/components/PageHeader/PageTitleIcon/semantics';

interface PostPageTitleIconDataInput {
  iconAPI: string;
  iconStatusAPI?: string;
  isActive?: boolean;
  id: string;
  apiCallFailureMessage: string;
  apiCallSuccessMessage: string;
}

export interface UsePageTitleIconDataResponse {
  toggled: boolean;
  showFailBanner: boolean;
  showSuccessBanner: boolean;
  requestFailMessage: string | null;
  requestSuccessMessage: string | null;
  pageTitleIconToggleEvent: () => void;
}

interface PageTitleIconData {
  id: string;
  toggled: boolean;
}

export interface PageTitleIconApiResponse {
  errorMessage?: string;
  successMessage?: string;
}

export interface PageTitleIconStatusApiResponse {
  isActive: boolean;
}

export const ajaxPostPageTitleIconInfo = async (
  url: string,
  data: PageTitleIconData,
  timeout?: number,
  headers?: Record<string, unknown>,
): Promise<PageTitleIconApiResponse | null> => {
  try {
    const response = await postRequestWithRetry({
      data,
      url,
      timeout,
      headers,
    });
    return response.data;
  } catch (error: any) {
    logger.info(
      `asyncRequests - page title icon post request for endpoint ${url}. error: ${error}`,
    );
    return null;
  }
};

export const ajaxGetPageTitleIconStatus = async (
  url: string,
  timeout?: number,
  headers?: Record<string, unknown>,
): Promise<PageTitleIconStatusApiResponse | null> => {
  try {
    const response = await requestWithRetry({
      url,
      timeout,
      headers,
    });
    return response.data;
  } catch (error: any) {
    logger.info(
      `asyncRequests - page title icon status post request for endpoint ${url}. error: ${error}`,
    );
    return null;
  }
};

export const usePageTitleIconData = ({
  iconAPI,
  iconStatusAPI,
  isActive,
  id,
  apiCallFailureMessage,
  apiCallSuccessMessage,
}: PostPageTitleIconDataInput): UsePageTitleIconDataResponse => {
  const [toggled, setToggled] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(true);
  const [showFailBanner, setShowFailBanner] = useState(true);
  const [requestFailMessage, setRequestFailMessage] = useState<string | null>(
    null,
  );
  const [requestSuccessMessage, setRequestSuccessMessage] = useState<
    string | null
  >(null);
  const { setPageTitleIconToggleData } = usePageTitleIconContext();

  useEffect(() => {
    if (iconStatusAPI) {
      (async () => {
        const res = await ajaxGetPageTitleIconStatus(iconStatusAPI);
        if (res) {
          setToggled(res.isActive);
        }
      })();
    } else {
      setToggled(!!isActive);
    }
  }, [isActive, iconStatusAPI]);

  const pageTitleIconToggleEvent = async () => {
    const initialToggled = toggled;
    setToggled(!toggled);
    const apiResponse = await ajaxPostPageTitleIconInfo(
      iconAPI,
      { id, toggled: !toggled },
      undefined,
      {
        'Content-type': 'application/json; charset=UTF-8',
      },
    );
    if (!apiResponse || apiResponse.errorMessage) {
      setRequestFailMessage(apiResponse?.errorMessage ?? apiCallFailureMessage);
      setShowFailBanner(true);
      setShowSuccessBanner(false);
      setToggled(initialToggled);
      return;
    } else {
      setRequestSuccessMessage(
        apiResponse?.successMessage ?? apiCallSuccessMessage,
      );
      setShowFailBanner(false);
      setShowSuccessBanner(true);
    }
    setPageTitleIconToggleData({
      apiResponse: apiResponse,
    });
    emitPageTitleIconToggle(!initialToggled);
  };

  return {
    toggled,
    showFailBanner,
    showSuccessBanner,
    requestFailMessage,
    requestSuccessMessage,
    pageTitleIconToggleEvent,
  };
};
